import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';
import { Check, Crown, Envelope, ArrowSquareOut } from '@phosphor-icons/react';

const M = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.08)',
  borderStrong: 'rgba(247,246,242,0.2)',
};

export function AdminPlans() {
  const { session } = useAuth();
  const { plan, isPro, subscriptionEnd, loading: planLoading } = usePlan();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async (interval: 'month' | 'year') => {
    if (!session) return;
    setCheckoutLoading(interval);
    setCheckoutError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { plan: 'pro', interval },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.open(data.url, '_blank');
      else throw new Error('Nenhuma URL de checkout retornada');
    } catch (e: any) {
      console.error('Checkout error:', e);
      setCheckoutError(e?.message || 'Erro ao iniciar checkout. Tente novamente.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    if (!session) return;
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (e) {
      console.error('Portal error:', e);
    } finally {
      setPortalLoading(false);
    }
  };

  if (planLoading) return <p style={{ fontFamily: M, color: C.fgMuted }}>Carregando...</p>;

  const plans = [
    {
      name: 'GRÁTIS',
      key: 'free',
      price: 'R$0',
      period: '',
      features: [
        '5 galerias máximo',
        '10 imagens por galeria',
        'Aparência básica',
        'Badge CRIAAA visível',
        'Sem custom domain',
      ],
      current: plan === 'free',
    },
    {
      name: 'PRO',
      key: 'pro',
      price: 'R$59,90',
      period: '/mês',
      yearlyPrice: 'R$599,00/ano',
      features: [
        'Galerias ilimitadas',
        'Imagens ilimitadas',
        'Aparência completa',
        'Badge removível',
        'Custom domain',
      ],
      current: plan === 'pro',
      highlight: true,
    },
    {
      name: 'CUSTOM',
      key: 'custom',
      price: 'Sob consulta',
      period: '',
      features: [
        'Tudo do Pro',
        'Design personalizado',
        'Suporte prioritário',
        'Funcionalidades exclusivas',
      ],
      current: plan === 'custom',
    },
  ];

  return (
    <div>
      <h2 className="uppercase text-sm font-light tracking-wider mb-8" style={{ fontFamily: M, color: C.fg }}>
        PLANOS
      </h2>

      {isPro && subscriptionEnd && (
        <div style={{ marginBottom: 24, padding: 16, border: `1px solid ${C.borderStrong}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Crown size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.45)' }} />
            <span style={{ fontFamily: M, fontSize: 13, color: C.fg }}>Plano Pro ativo</span>
          </div>
          <p style={{ fontFamily: M, fontSize: 12, color: C.fgMuted, marginBottom: 12 }}>
            Próxima renovação: {new Date(subscriptionEnd).toLocaleDateString('pt-BR')}
          </p>
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            style={{
              fontFamily: M, fontSize: 12, fontWeight: 300,
              padding: '8px 16px',
              border: `1px solid ${C.borderStrong}`,
              backgroundColor: 'transparent',
              color: C.fg,
              cursor: 'pointer',
              textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <ArrowSquareOut size={12} weight="thin" />
            {portalLoading ? 'Carregando...' : 'Gerenciar Assinatura'}
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
        {plans.map((p) => (
          <div
            key={p.key}
            style={{
              border: `1px solid ${p.current ? C.fgMuted : p.highlight ? C.borderStrong : C.border}`,
              padding: 24,
              position: 'relative',
              backgroundColor: p.current ? 'rgba(247,246,242,0.03)' : 'transparent',
            }}
          >
            {p.current && (
              <span style={{
                position: 'absolute', top: -1, right: 16,
                fontFamily: M, fontSize: 12, fontWeight: 400,
                padding: '2px 8px',
                backgroundColor: 'rgba(247,246,242,0.15)',
                color: '#000',
                textTransform: 'uppercase',
              }}>
                Atual
              </span>
            )}

            <h3 style={{ fontFamily: M, fontSize: 16, fontWeight: 400, color: '#F7F6F2', marginBottom: 4 }}>
              {p.name}
            </h3>
            <div style={{ fontFamily: M, fontSize: 20, fontWeight: 300, color: C.fg, marginBottom: 4 }}>
              {p.price}<span style={{ fontSize: 12, color: C.fgMuted }}>{p.period}</span>
            </div>
            {p.yearlyPrice && (
              <p style={{ fontFamily: M, fontSize: 12, color: C.fgMuted, marginBottom: 16 }}>
                ou {p.yearlyPrice}
              </p>
            )}

            <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 24px' }}>
              {p.features.map((f, i) => (
                <li key={i} style={{
                  fontFamily: M, fontSize: 12, fontWeight: 300,
                  color: C.fgMuted, padding: '4px 0',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Check size={12} weight="thin" style={{ color: p.highlight ? '#00E87A' : C.fgMuted, flexShrink: 0 }} />
                  {f}
                </li>
              ))}
            </ul>

            {p.key === 'free' && !p.current && null}

            {p.key === 'pro' && !p.current && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  onClick={() => handleCheckout('month')}
                  disabled={checkoutLoading !== null}
                  style={{
                    fontFamily: M, fontSize: 12, fontWeight: 400,
                    padding: '10px 16px',
                    backgroundColor: '#F7F6F2', color: '#000000',
                    border: 'none', cursor: 'pointer',
                    textTransform: 'uppercase',
                  }}
                >
                  {checkoutLoading === 'month' ? 'Carregando...' : 'ASSINAR MENSAL'}
                </button>
                <button
                  onClick={() => handleCheckout('year')}
                  disabled={checkoutLoading !== null}
                  style={{
                    fontFamily: M, fontSize: 12, fontWeight: 400,
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    color: C.fg,
                    border: `1px solid ${C.borderStrong}`,
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                  }}
                >
                  {checkoutLoading === 'year' ? 'Carregando...' : 'ASSINAR ANUAL'}
                </button>
                {checkoutError && (
                  <p style={{ fontFamily: M, fontSize: 12, color: '#ff6b6b', marginTop: 4 }}>
                    {checkoutError}
                  </p>
                )}
              </div>
            )}

            {p.key === 'custom' && !p.current && (
              <a
                href="mailto:contato@criaaa.com"
                style={{
                  fontFamily: M, fontSize: 12, fontWeight: 400,
                  padding: '10px 16px',
                  border: `1px solid ${C.borderStrong}`,
                  backgroundColor: 'transparent', color: C.fg,
                  textTransform: 'uppercase',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  textDecoration: 'none',
                }}
              >
                <Envelope size={12} weight="thin" />
                ENTRAR EM CONTATO
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
